import { logger } from './logger';

/** Test admin user ID — leads & channel partners are auto-shared with this user */
export const TEST_ADMIN_USER_ID = 'user_admin_001';

/**
 * List of Indian States and Union Territories
 */
export const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
] as const;

/**
 * List of major Indian Cities
 */
export const INDIAN_CITIES = [
  'Agra',
  'Ahmedabad',
  'Ajmer',
  'Aligarh',
  'Allahabad',
  'Amravati',
  'Amritsar',
  'Aurangabad',
  'Bareilly',
  'Belgaum',
  'Bengaluru',
  'Bhilai',
  'Bhopal',
  'Bhubaneswar',
  'Bikaner',
  'Chandigarh',
  'Chennai',
  'Coimbatore',
  'Cuttack',
  'Dehradun',
  'Delhi',
  'Dhanbad',
  'Durgapur',
  'Erode',
  'Faridabad',
  'Firozabad',
  'Ghaziabad',
  'Gorakhpur',
  'Gulbarga',
  'Guntur',
  'Gurgaon',
  'Guwahati',
  'Gwalior',
  'Hubli',
  'Hyderabad',
  'Indore',
  'Jabalpur',
  'Jaipur',
  'Jalandhar',
  'Jammu',
  'Jamnagar',
  'Jamshedpur',
  'Jhansi',
  'Jodhpur',
  'Kanpur',
  'Kochi',
  'Kolhapur',
  'Kolkata',
  'Kota',
  'Kozhikode',
  'Kurnool',
  'Lucknow',
  'Ludhiana',
  'Madurai',
  'Mangalore',
  'Meerut',
  'Moradabad',
  'Mumbai',
  'Mysuru',
  'Nagpur',
  'Nashik',
  'Navi Mumbai',
  'Nellore',
  'Noida',
  'Patna',
  'Pondicherry',
  'Pune',
  'Raipur',
  'Rajkot',
  'Ranchi',
  'Rohtak',
  'Salem',
  'Sangli',
  'Shimla',
  'Siliguri',
  'Solapur',
  'Srinagar',
  'Surat',
  'Thane',
  'Thiruvananthapuram',
  'Thrissur',
  'Tiruchirappalli',
  'Tiruppur',
  'Udaipur',
  'Ujjain',
  'Vadodara',
  'Varanasi',
  'Vijayawada',
  'Visakhapatnam',
  'Warangal',
] as const;

/**
 * Normalize phone number to +91XXXXXXXXXX format
 * Ensures consistent storage format across the application
 * @param phone - Phone number in any format (10 digits, +91XXXXXXXXXX, etc.)
 * @returns Normalized phone number in +91XXXXXXXXXX format
 */
export const normalizePhoneNumber = (phone: string): string => {
  logger.debug('Phone normalization started', { input: phone });
  
  // Remove all non-digit characters except leading +
  let normalized = phone.replace(/[^\d+]/g, '');
  
  // Remove any + signs
  normalized = normalized.replace(/\+/g, '');
  
  // Remove leading 91 if present (we'll add it back)
  if (normalized.startsWith('91') && normalized.length > 10) {
    normalized = normalized.substring(2);
  }
  
  // Take only last 10 digits (Indian mobile number)
  if (normalized.length > 10) {
    normalized = normalized.substring(normalized.length - 10);
  }
  
  // Add +91 prefix
  const result = `+91${normalized}`;
  logger.debug('Phone normalization completed', { input: phone, output: result });
  
  return result;
};

/**
 * World countries list (excluding India) for NRI country selection
 */
export const WORLD_COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia',
  'Australia', 'Austria', 'Azerbaijan', 'Bahrain', 'Bangladesh', 'Belgium', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
  'Cambodia', 'Cameroon', 'Canada', 'Chile', 'China', 'Colombia', 'Congo',
  'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Ecuador',
  'Egypt', 'Ethiopia', 'Finland', 'France', 'Georgia', 'Germany', 'Ghana', 'Greece',
  'Guatemala', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'Indonesia', 'Iran',
  'Iraq', 'Ireland', 'Israel', 'Italy', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Libya', 'Lithuania',
  'Luxembourg', 'Macau', 'Malaysia', 'Maldives', 'Malta', 'Mauritius', 'Mexico',
  'Moldova', 'Mongolia', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nepal',
  'Netherlands', 'New Zealand', 'Nigeria', 'North Korea', 'Norway', 'Oman',
  'Pakistan', 'Panama', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saudi Arabia', 'Senegal', 'Serbia',
  'Singapore', 'Slovakia', 'Slovenia', 'Somalia', 'South Africa', 'South Korea',
  'Spain', 'Sri Lanka', 'Sudan', 'Sweden', 'Switzerland', 'Syria', 'Taiwan',
  'Tajikistan', 'Tanzania', 'Thailand', 'Tunisia', 'Turkey', 'Turkmenistan',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
] as const;

/**
 * Calling code + national mobile-number digit length (min/max, after the
 * calling code) for every WORLD_COUNTRIES entry, so the NRI mobile/alternate
 * number fields can show the right "+<code>" prefix and accept the right
 * number of digits instead of always assuming India's "+91" / 10 digits.
 * Lengths are the typical mobile-number length for that country (approximate
 * for a few countries with variable-length numbering plans).
 */
export interface CountryPhoneInfo {
  dialCode: string;
  minLength: number;
  maxLength: number;
}

export const INDIA_PHONE_INFO: CountryPhoneInfo = { dialCode: '91', minLength: 10, maxLength: 10 };

export const COUNTRY_PHONE_INFO: Record<string, CountryPhoneInfo> = {
  'Afghanistan': { dialCode: '93', minLength: 9, maxLength: 9 },
  'Albania': { dialCode: '355', minLength: 9, maxLength: 9 },
  'Algeria': { dialCode: '213', minLength: 9, maxLength: 9 },
  'Andorra': { dialCode: '376', minLength: 6, maxLength: 6 },
  'Angola': { dialCode: '244', minLength: 9, maxLength: 9 },
  'Argentina': { dialCode: '54', minLength: 10, maxLength: 11 },
  'Armenia': { dialCode: '374', minLength: 8, maxLength: 8 },
  'Australia': { dialCode: '61', minLength: 9, maxLength: 9 },
  'Austria': { dialCode: '43', minLength: 7, maxLength: 13 },
  'Azerbaijan': { dialCode: '994', minLength: 9, maxLength: 9 },
  'Bahrain': { dialCode: '973', minLength: 8, maxLength: 8 },
  'Bangladesh': { dialCode: '880', minLength: 10, maxLength: 10 },
  'Belgium': { dialCode: '32', minLength: 9, maxLength: 9 },
  'Bhutan': { dialCode: '975', minLength: 8, maxLength: 8 },
  'Bolivia': { dialCode: '591', minLength: 8, maxLength: 8 },
  'Bosnia and Herzegovina': { dialCode: '387', minLength: 8, maxLength: 8 },
  'Botswana': { dialCode: '267', minLength: 7, maxLength: 8 },
  'Brazil': { dialCode: '55', minLength: 10, maxLength: 11 },
  'Brunei': { dialCode: '673', minLength: 7, maxLength: 7 },
  'Bulgaria': { dialCode: '359', minLength: 8, maxLength: 9 },
  'Cambodia': { dialCode: '855', minLength: 8, maxLength: 9 },
  'Cameroon': { dialCode: '237', minLength: 9, maxLength: 9 },
  'Canada': { dialCode: '1', minLength: 10, maxLength: 10 },
  'Chile': { dialCode: '56', minLength: 9, maxLength: 9 },
  'China': { dialCode: '86', minLength: 11, maxLength: 11 },
  'Colombia': { dialCode: '57', minLength: 10, maxLength: 10 },
  'Congo': { dialCode: '242', minLength: 9, maxLength: 9 },
  'Costa Rica': { dialCode: '506', minLength: 8, maxLength: 8 },
  'Croatia': { dialCode: '385', minLength: 8, maxLength: 9 },
  'Cuba': { dialCode: '53', minLength: 8, maxLength: 8 },
  'Cyprus': { dialCode: '357', minLength: 8, maxLength: 8 },
  'Czech Republic': { dialCode: '420', minLength: 9, maxLength: 9 },
  'Denmark': { dialCode: '45', minLength: 8, maxLength: 8 },
  'Ecuador': { dialCode: '593', minLength: 9, maxLength: 9 },
  'Egypt': { dialCode: '20', minLength: 10, maxLength: 10 },
  'Ethiopia': { dialCode: '251', minLength: 9, maxLength: 9 },
  'Finland': { dialCode: '358', minLength: 7, maxLength: 10 },
  'France': { dialCode: '33', minLength: 9, maxLength: 9 },
  'Georgia': { dialCode: '995', minLength: 9, maxLength: 9 },
  'Germany': { dialCode: '49', minLength: 10, maxLength: 11 },
  'Ghana': { dialCode: '233', minLength: 9, maxLength: 9 },
  'Greece': { dialCode: '30', minLength: 10, maxLength: 10 },
  'Guatemala': { dialCode: '502', minLength: 8, maxLength: 8 },
  'Honduras': { dialCode: '504', minLength: 8, maxLength: 8 },
  'Hong Kong': { dialCode: '852', minLength: 8, maxLength: 8 },
  'Hungary': { dialCode: '36', minLength: 9, maxLength: 9 },
  'Iceland': { dialCode: '354', minLength: 7, maxLength: 7 },
  'Indonesia': { dialCode: '62', minLength: 9, maxLength: 12 },
  'Iran': { dialCode: '98', minLength: 10, maxLength: 10 },
  'Iraq': { dialCode: '964', minLength: 10, maxLength: 10 },
  'Ireland': { dialCode: '353', minLength: 9, maxLength: 9 },
  'Israel': { dialCode: '972', minLength: 9, maxLength: 9 },
  'Italy': { dialCode: '39', minLength: 9, maxLength: 10 },
  'Japan': { dialCode: '81', minLength: 10, maxLength: 10 },
  'Jordan': { dialCode: '962', minLength: 9, maxLength: 9 },
  'Kazakhstan': { dialCode: '7', minLength: 10, maxLength: 10 },
  'Kenya': { dialCode: '254', minLength: 9, maxLength: 9 },
  'Kuwait': { dialCode: '965', minLength: 8, maxLength: 8 },
  'Kyrgyzstan': { dialCode: '996', minLength: 9, maxLength: 9 },
  'Laos': { dialCode: '856', minLength: 8, maxLength: 10 },
  'Latvia': { dialCode: '371', minLength: 8, maxLength: 8 },
  'Lebanon': { dialCode: '961', minLength: 7, maxLength: 8 },
  'Libya': { dialCode: '218', minLength: 9, maxLength: 9 },
  'Lithuania': { dialCode: '370', minLength: 8, maxLength: 8 },
  'Luxembourg': { dialCode: '352', minLength: 6, maxLength: 9 },
  'Macau': { dialCode: '853', minLength: 8, maxLength: 8 },
  'Malaysia': { dialCode: '60', minLength: 9, maxLength: 10 },
  'Maldives': { dialCode: '960', minLength: 7, maxLength: 7 },
  'Malta': { dialCode: '356', minLength: 8, maxLength: 8 },
  'Mauritius': { dialCode: '230', minLength: 7, maxLength: 8 },
  'Mexico': { dialCode: '52', minLength: 10, maxLength: 10 },
  'Moldova': { dialCode: '373', minLength: 8, maxLength: 8 },
  'Mongolia': { dialCode: '976', minLength: 8, maxLength: 8 },
  'Morocco': { dialCode: '212', minLength: 9, maxLength: 9 },
  'Mozambique': { dialCode: '258', minLength: 9, maxLength: 9 },
  'Myanmar': { dialCode: '95', minLength: 8, maxLength: 10 },
  'Namibia': { dialCode: '264', minLength: 9, maxLength: 9 },
  'Nepal': { dialCode: '977', minLength: 10, maxLength: 10 },
  'Netherlands': { dialCode: '31', minLength: 9, maxLength: 9 },
  'New Zealand': { dialCode: '64', minLength: 8, maxLength: 10 },
  'Nigeria': { dialCode: '234', minLength: 10, maxLength: 10 },
  'North Korea': { dialCode: '850', minLength: 8, maxLength: 10 },
  'Norway': { dialCode: '47', minLength: 8, maxLength: 8 },
  'Oman': { dialCode: '968', minLength: 8, maxLength: 8 },
  'Pakistan': { dialCode: '92', minLength: 10, maxLength: 10 },
  'Panama': { dialCode: '507', minLength: 8, maxLength: 8 },
  'Paraguay': { dialCode: '595', minLength: 9, maxLength: 9 },
  'Peru': { dialCode: '51', minLength: 9, maxLength: 9 },
  'Philippines': { dialCode: '63', minLength: 10, maxLength: 10 },
  'Poland': { dialCode: '48', minLength: 9, maxLength: 9 },
  'Portugal': { dialCode: '351', minLength: 9, maxLength: 9 },
  'Qatar': { dialCode: '974', minLength: 8, maxLength: 8 },
  'Romania': { dialCode: '40', minLength: 9, maxLength: 9 },
  'Russia': { dialCode: '7', minLength: 10, maxLength: 10 },
  'Rwanda': { dialCode: '250', minLength: 9, maxLength: 9 },
  'Saudi Arabia': { dialCode: '966', minLength: 9, maxLength: 9 },
  'Senegal': { dialCode: '221', minLength: 9, maxLength: 9 },
  'Serbia': { dialCode: '381', minLength: 8, maxLength: 9 },
  'Singapore': { dialCode: '65', minLength: 8, maxLength: 8 },
  'Slovakia': { dialCode: '421', minLength: 9, maxLength: 9 },
  'Slovenia': { dialCode: '386', minLength: 8, maxLength: 8 },
  'Somalia': { dialCode: '252', minLength: 7, maxLength: 8 },
  'South Africa': { dialCode: '27', minLength: 9, maxLength: 9 },
  'South Korea': { dialCode: '82', minLength: 9, maxLength: 10 },
  'Spain': { dialCode: '34', minLength: 9, maxLength: 9 },
  'Sri Lanka': { dialCode: '94', minLength: 9, maxLength: 9 },
  'Sudan': { dialCode: '249', minLength: 9, maxLength: 9 },
  'Sweden': { dialCode: '46', minLength: 7, maxLength: 9 },
  'Switzerland': { dialCode: '41', minLength: 9, maxLength: 9 },
  'Syria': { dialCode: '963', minLength: 9, maxLength: 9 },
  'Taiwan': { dialCode: '886', minLength: 9, maxLength: 9 },
  'Tajikistan': { dialCode: '992', minLength: 9, maxLength: 9 },
  'Tanzania': { dialCode: '255', minLength: 9, maxLength: 9 },
  'Thailand': { dialCode: '66', minLength: 9, maxLength: 9 },
  'Tunisia': { dialCode: '216', minLength: 8, maxLength: 8 },
  'Turkey': { dialCode: '90', minLength: 10, maxLength: 10 },
  'Turkmenistan': { dialCode: '993', minLength: 8, maxLength: 8 },
  'Uganda': { dialCode: '256', minLength: 9, maxLength: 9 },
  'Ukraine': { dialCode: '380', minLength: 9, maxLength: 9 },
  'United Arab Emirates': { dialCode: '971', minLength: 9, maxLength: 9 },
  'United Kingdom': { dialCode: '44', minLength: 10, maxLength: 10 },
  'United States': { dialCode: '1', minLength: 10, maxLength: 10 },
  'Uruguay': { dialCode: '598', minLength: 8, maxLength: 9 },
  'Uzbekistan': { dialCode: '998', minLength: 9, maxLength: 9 },
  'Venezuela': { dialCode: '58', minLength: 10, maxLength: 10 },
  'Vietnam': { dialCode: '84', minLength: 9, maxLength: 10 },
  'Yemen': { dialCode: '967', minLength: 9, maxLength: 9 },
  'Zambia': { dialCode: '260', minLength: 9, maxLength: 9 },
  'Zimbabwe': { dialCode: '263', minLength: 9, maxLength: 10 },
};

/**
 * Look up calling code + digit length for a WORLD_COUNTRIES entry, falling
 * back to a generic international range if the country isn't in the map.
 */
export function getCountryPhoneInfo(country: string | undefined | null): CountryPhoneInfo {
  if (country && COUNTRY_PHONE_INFO[country]) return COUNTRY_PHONE_INFO[country];
  return { dialCode: '', minLength: 4, maxLength: 14 };
}
